// Route and tab contract of the redesigned app shell (SPEC decision 4).
// Route names are stack screens registered in navigation/AuthenticatedAppStack.js.

export const DRIVER_ROUTE = {
	HOME: 'Home',
	START_OF_DAY: 'StartOfDayView',
	// Start of day step pages (pushed from StartOfDayView, so they keep its tab).
	RUN_OVERVIEW: 'RunOverviewView',
	KEYS_ACCESS: 'KeysAccessView',
	RECALC_PICK: 'RecalcPickView',
	PRODUCT_CHANGES: 'ProductChangesView',
	RUN_LIST: 'RunListView',
	STOP: 'StopView',
	STOP_MACHINE: 'StopMachineView',
	REORDER_STOPS: 'ReorderStopsView',
	END_OF_DAY: 'EndOfDayView',
	PICKING_MY_DAY: 'PickingMyDayView',
	ALL_RUNS: 'AllRunsView',
	MY_RUN_OVERVIEW: 'MyRunOverviewView',
	EMPLOYEE_DAY_PREVIEW: 'EmployeeDayPreviewView',
};

// Shown (with Try again) when today's run failed to load and nothing is cached.
export const RUN_LOAD_ERROR = 'Today\'s run could not be loaded. Check your connection and try again.';

export const APP_TAB = {
	HOME: 'home',
	START_OF_DAY: 'startOfDay',
	ON_THE_RUN: 'onTheRun',
	END_OF_DAY: 'endOfDay',
	PICKING_MY_DAY: 'pickingMyDay',
	ALL_RUNS: 'allRuns',
};

// Which bottom bar a user gets (computed by DriverDayProvider).
export const TAB_BAR_VARIANT = {
	DRIVER: 'driver', // Home | Start of day | On the run | End of day
	PICKER: 'picker', // Home | Picking My Day | All Runs
	BASIC: 'basic', // Home | All Runs
};

export const APP_TABS = {
	[APP_TAB.HOME]: { label: 'Home', icon: 'home', route: DRIVER_ROUTE.HOME },
	[APP_TAB.START_OF_DAY]: { label: 'Start of day', icon: 'sunrise', route: DRIVER_ROUTE.START_OF_DAY },
	[APP_TAB.ON_THE_RUN]: { label: 'On the run', icon: 'truck', route: DRIVER_ROUTE.RUN_LIST },
	[APP_TAB.END_OF_DAY]: { label: 'End of day', icon: 'moon', route: DRIVER_ROUTE.END_OF_DAY },
	[APP_TAB.PICKING_MY_DAY]: { label: 'Picking My Day', icon: 'package', route: DRIVER_ROUTE.PICKING_MY_DAY },
	[APP_TAB.ALL_RUNS]: { label: 'All Runs', icon: 'list', route: DRIVER_ROUTE.ALL_RUNS },
};

export const TAB_BAR_TABS = {
	[TAB_BAR_VARIANT.DRIVER]: [APP_TAB.HOME, APP_TAB.START_OF_DAY, APP_TAB.ON_THE_RUN, APP_TAB.END_OF_DAY],
	[TAB_BAR_VARIANT.PICKER]: [APP_TAB.HOME, APP_TAB.PICKING_MY_DAY, APP_TAB.ALL_RUNS],
	[TAB_BAR_VARIANT.BASIC]: [APP_TAB.HOME, APP_TAB.ALL_RUNS],
};

// Routes that belong to a tab wherever they are opened from. Any other route
// takes the tab of the nearest route below it in the stack, so pushed step
// pages (pick list, checklist, tickets, route map, reorder...) highlight the
// phase that opened them. Wave-2 screens that can be opened from outside their
// phase (e.g. from Home) add their route names here.
export const ROUTE_TAB = {
	[DRIVER_ROUTE.HOME]: APP_TAB.HOME,
	[DRIVER_ROUTE.START_OF_DAY]: APP_TAB.START_OF_DAY,
	[DRIVER_ROUTE.RUN_LIST]: APP_TAB.ON_THE_RUN,
	[DRIVER_ROUTE.STOP]: APP_TAB.ON_THE_RUN,
	[DRIVER_ROUTE.END_OF_DAY]: APP_TAB.END_OF_DAY,
	[DRIVER_ROUTE.PICKING_MY_DAY]: APP_TAB.PICKING_MY_DAY,
	[DRIVER_ROUTE.ALL_RUNS]: APP_TAB.ALL_RUNS,
	ServiceRunListView: APP_TAB.ALL_RUNS,
};

// Route param for a stop that is not part of the user's driver day (another
// day's or driver's stop opened from All Runs): it keeps the opener's tab
// instead of On the run.
export const KEEP_OPENER_TAB_PARAM = 'keepOpenerTab';

// Full-screen routes without the bottom bar (camera, scanner and image
// viewers elsewhere are modals that already cover it).
export const TAB_BAR_HIDDEN_ROUTES = new Set(['BarcodeScannerView']);

/**
 * Active tab for a stack (array of routes, bottom first): the tab of the
 * topmost route that maps to a tab; Home when no route does. A page whose tab
 * is not on this bar (All Runs opened from the side menu on the driver bar)
 * highlights nothing rather than a Home the user is not on.
 */
export const getActiveTab = (routes, visibleTabs) => {
	for (let index = (routes || []).length - 1; index >= 0; index -= 1) {
		const route = routes[index];
		const tab = route?.params?.[KEEP_OPENER_TAB_PARAM] ? null : ROUTE_TAB[route?.name];
		if (tab) {
			return visibleTabs.includes(tab) ? tab : null;
		}
	}

	return APP_TAB.HOME;
};
